import { GridCustomCellProps } from '@progress/kendo-react-grid';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import useBreakpoints from '../../../../../../common/hooks/useBreakpoints';
import Button from '../../../../../../common/widgets/Button';
import { GridCellTd } from '../../../../../../common/widgets/Grid';
import Icons from '../../../../../../common/widgets/Icons';
import PopoverDialog from '../../../../../../common/widgets/PopoverDialog';
import { resources } from '../../../../resources/resources';
import { FinalReportGridItemModel, ReportFileContentTypes } from '../types';

interface ReportTypeNameCellProps extends GridCustomCellProps {
  isOneCompany?: boolean;
}

export function ReportTypeNameCell(props: Readonly<ReportTypeNameCellProps>) {
  const { dataItem, isOneCompany } = props;
  const { t: translate } = useTranslation();
  const fileContentTypeToResources: { [key: number]: string } = {
    [ReportFileContentTypes.FinalReport]: translate(
      resources.admin.finalReport.grid.finalReport,
    ),
    [ReportFileContentTypes.FinalMonthlyInvoices]: translate(
      resources.admin.finalReport.grid.finalMonthlyInvoices,
    ),
    [ReportFileContentTypes.AdvanceInvoice]: translate(
      resources.admin.finalReport.grid.advanceInvoice,
    ),
  };

  return (
    <GridCellTd {...props.tdProps}>
      <div className="final-report-name-container">
        <span className="final-report-svg-container">
          {dataItem.canDownload ? (
            <Icons.ReportDone />
          ) : (
            <Icons.ReportInProcess />
          )}
        </span>
        <div className="final-report-name-right-container">
          <span className="final-reports-grid-name">
            {fileContentTypeToResources[dataItem.fileContentTypeId]}
            {!dataItem.canDownload && (
              <span
                className="grid-status-badge"
                style={{
                  backgroundColor: '#FFECC5',
                }}
              >
                {translate(resources.admin.finalReport.processing)}
              </span>
            )}
          </span>

          {!isOneCompany && (
            <span className="final-reports-grid-company">
              {dataItem.company
                ? dataItem.company
                : translate(resources.admin.employees.filters.companyDefault)}
            </span>
          )}
        </div>
      </div>
    </GridCellTd>
  );
}

export function DateCell(props: GridCustomCellProps) {
  const { dataItem } = props;
  return <GridCellTd>{moment(dataItem.date).format('MMM YYYY')}</GridCellTd>;
}

export function CompanyCell(props: GridCustomCellProps) {
  const { dataItem } = props;
  const { t: translate } = useTranslation();
  return (
    <GridCellTd>
      {dataItem.company ?? translate(resources.admin.finalReport.allCompanies)}
    </GridCellTd>
  );
}

export function DateAddedCell(props: GridCustomCellProps) {
  const { dataItem } = props;
  return (
    <GridCellTd>
      {moment(dataItem.dateAdded).format('DD/MM/YYYY LT')}
    </GridCellTd>
  );
}

export function ActionsCell(props: GridCustomCellProps) {
  const { dataItem } = props;
  const { t: translate } = useTranslation();
  const { isTablet } = useBreakpoints();

  return (
    <GridCellTd className="grid-actions-cell">
      {!isTablet ? (
        <span className="grid-actions column-display">
          <Button
            className="btn-link see-all-btn"
            onClick={() =>
              props.dataItem.onDownload(dataItem as FinalReportGridItemModel)
            }
            disabled={!dataItem.canDownload}
            icon={<Icons.ArrowButton />}
            title={
              dataItem.canDownload
                ? undefined
                : translate(resources.admin.finalReport.downloadButtonTooltip)
            }
            loading={dataItem.isDownloading}
          >
            <span className="btn-text">
              {translate(resources.admin.finalReport.grid.actions.download)}
            </span>
          </Button>
          {dataItem.canDelete && (
            <Button
              className="btn-link see-all-btn"
              onClick={() => {
                props.dataItem.onDelete(
                  dataItem.reportId,
                  dataItem.fileContentTypeId,
                );
              }}
              icon={<Icons.DeleteButton />}
              loading={dataItem.isDeleting}
            >
              <span className="btn-text">
                {translate(resources.admin.finalReport.grid.actions.delete)}
              </span>
            </Button>
          )}
        </span>
      ) : (
        <PopoverDialog
          className="popover-actions-list"
          placement="bottom"
          closeFromContent
          type="click"
          appendTo={document.body}
          content={
            <>
              {dataItem.canDownload && (
                <Button
                  className="btn"
                  onClick={() =>
                    props.dataItem.onDownload(
                      dataItem as FinalReportGridItemModel,
                    )
                  }
                >
                  <span className="btn-text">
                    {translate(
                      resources.admin.finalReport.grid.actions.download,
                    )}
                  </span>
                </Button>
              )}
              {dataItem.canDelete && (
                <Button
                  className="btn"
                  onClick={() => {
                    props.dataItem.onDelete(
                      dataItem.reportId,
                      dataItem.fileContentTypeId,
                    );
                  }}
                >
                  <span className="btn-text">
                    {translate(resources.admin.finalReport.grid.actions.delete)}
                  </span>
                </Button>
              )}
            </>
          }
        >
          <a className="mobile-admin-action"></a>
        </PopoverDialog>
      )}
    </GridCellTd>
  );
}
